import { useEffect, useState } from 'react';
import { set, setWith } from 'unchanged';

import routes from 'js/core_pre_webpack/routes';
import { getCsrfToken } from '../util';
import { User, Work } from './types';

type UseRelatedWorksInterface = {
  bookmarkWork: (work: Work) => void;
  followWorkAuthor: (author: User) => void;
  setRelatedWorks: (authors: Work[]) => void;
  relatedWorks: Work[];
};

const toggle = setWith((val: boolean) => !val);

export const useRelatedWorks = (
  initialWorks: Work[] = [],
): UseRelatedWorksInterface => {
  // This is one author of a related work to follow/unfollow
  // We update all related works in our list in case they're by the same author
  // to make sure all the author cards are consistent.
  //
  // Eg, work 1 has author with id 123
  // and work 2 has authors with id 123 and 234.
  // We'll map over all related works to update them,
  // so author 123 will show as "Following" in the popover for
  // work 1 and 2.
  const [authorToToggle, setAuthorToToggle] = useState<User | null>(null);
  // This is a work that we want to bookmark/unbookmark from a user's library
  const [workToToggle, setWorkToToggle] = useState<Work | null>(null);
  const [relatedWorks, setRelatedWorks] = useState(initialWorks);

  // Effect for following an author
  useEffect(() => {
    if (authorToToggle == null) return;

    const route = routes.api_v0_user_following_index_path;
    // Route stays the same, but to follow we POST,
    // to unfollow, we DELETE
    const method = authorToToggle.following ? 'DELETE' : 'POST';

    fetch(route({ subdomain_param: 'api', user_id: authorToToggle.id }), {
      credentials: 'include',
      headers: {
        'X-CSRF-TOKEN': getCsrfToken(),
      },
      method,
    })
      .then((res) => {
        if (res.ok) {
          // This update is a bit more complicated than the useRis or useAuthors
          // just because we're updating a more nested piece of state
          setRelatedWorks((works) =>
            works.map((w) => ({
              ...w,
              authors: w.authors.map((a) => {
                if (a.id === authorToToggle.id) {
                  return toggle('following', a) as User;
                } else {
                  return a;
                }
              }),
            })),
          );
        }
      })
      .finally(() => setAuthorToToggle(null));
  }, [authorToToggle]);

  // Effect for bookmarking a work
  useEffect(() => {
    if (workToToggle == null) return;

    // If we have a bookmarkId, we should delete the bookmark
    // otherwise, we're adding a new one
    if (workToToggle.bookmarkId != null) {
      const route = routes.destroy_for_work_api_v0_bookmarks_path;

      fetch(
        route({ content_id: workToToggle.bookmarkId, subdomain_param: 'api' }),
        {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': getCsrfToken(),
          },
          method: 'DELETE',
        },
      )
        .then((res) => {
          if (res.ok) {
            setRelatedWorks((works) => {
              const i = works.findIndex((w) => w.id === workToToggle.id);

              return set([i, 'bookmarkId'], null, works) as Work[];
            });
          }
        })
        .finally(() => setWorkToToggle(null));
    } else {
      const route = routes.api_v0_bookmarks_path;

      fetch(
        route({
          subdomain_param: 'api',
        }),
        {
          body: JSON.stringify({
            bookmark: {
              content_id: workToToggle.id,
              content_type: 'Work',
              message: '',
              source: 'swp',
            },
          }),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          method: 'POST',
        },
      )
        .then(async (res) => {
          if (res.ok) {
            const resp = await res.json();
            const { id: bookmarkId } = resp.bookmark;

            setRelatedWorks((works) => {
              const i = works.findIndex((w) => w.id === workToToggle.id);
              return set([i, 'bookmarkId'], bookmarkId, works) as Work[];
            });
          }
        })
        .finally(() => setWorkToToggle(null));
    }
  }, [workToToggle]);

  const bookmarkWork = (work: Work) => {
    setWorkToToggle(work);
  };

  const followWorkAuthor = (author: User) => {
    setAuthorToToggle(author);
  };

  return { bookmarkWork, followWorkAuthor, relatedWorks, setRelatedWorks };
};
